/**
 * Shared option catalogs for MyAgentProfile inline-edit sidebars and the
 * full edit wizard. Single source of truth so the inline-edit drawers and
 * the wizard step forms always show the same option lists.
 *
 * Existing inline copies inside `wizard/BasicInfoStep.jsx` and
 * `wizard/SkillsLanguagesStep.jsx` will be migrated to import from here in
 * a follow-up; they're kept in place for now to avoid touching unrelated
 * files during the MyAgentProfile migration.
 */

// ─── Loan products ───────────────────────────────────────────────────────
export const LOAN_PRODUCTS = [
    { value: 'home_loan',        label: 'Home Loan',                 icon: 'pi-home',        color: 'blue' },
    { value: 'personal_loan',    label: 'Personal Loan',             icon: 'pi-user',        color: 'purple' },
    { value: 'business_loan',    label: 'Business Loan',             icon: 'pi-briefcase',   color: 'indigo' },
    { value: 'car_loan',         label: 'Car Loan',                  icon: 'pi-car',         color: 'teal' },
    { value: 'education_loan',   label: 'Education Loan',            icon: 'pi-book',        color: 'orange' },
    { value: 'gold_loan',        label: 'Gold Loan',                 icon: 'pi-star',        color: 'yellow' },
    { value: 'property_loan',    label: 'Loan Against Property',     icon: 'pi-building',    color: 'pink' },
    { value: 'credit_card',      label: 'Credit Card',               icon: 'pi-credit-card', color: 'cyan' },
    { value: 'working_capital',  label: 'Working Capital Loan',      icon: 'pi-chart-line',  color: 'green' },
    { value: 'msme_loan',        label: 'MSME Loan',                 icon: 'pi-shop',        color: 'red' },
];

// Lookup helper — backend stores product as { product_type } objects.
export const productLabel = (productType) =>
    LOAN_PRODUCTS.find((p) => p.value === productType)?.label || productType;

// ─── Professional roles ──────────────────────────────────────────────────
export const PROFESSIONAL_ROLES = [
    { value: 'banker',              label: 'Banker',                       icon: 'pi-building',   color: 'blue' },
    { value: 'borrower',            label: 'Borrower',                     icon: 'pi-user',       color: 'purple' },
    { value: 'dsa',                 label: 'DSA (Direct Selling Agent)',   icon: 'pi-users',      color: 'indigo' },
    { value: 'lawyer',              label: 'Lawyer',                       icon: 'pi-briefcase',  color: 'teal' },
    { value: 'ca',                  label: 'Chartered Accountant',         icon: 'pi-calculator', color: 'orange' },
    { value: 'property_consultant', label: 'Property Consultant',          icon: 'pi-home',       color: 'pink' },
    { value: 'insurance_advisor',   label: 'Insurance Advisor',            icon: 'pi-shield',     color: 'cyan' },
    { value: 'broker',              label: 'Broker',                       icon: 'pi-chart-line', color: 'green' },
];

export const roleLabel = (roleType) =>
    PROFESSIONAL_ROLES.find((r) => r.value === roleType)?.label || roleType;

// ─── Product-specific expertise (specializations) ────────────────────────
// Mirrors the wizard's PRODUCT_EXPERTISE_MAP. Used when the user wants to
// edit their `expertise` array — we flatten the map into a flat option list
// because the inline-edit Sidebar doesn't gate by selected product.
export const PRODUCT_EXPERTISE_MAP = {
    home_loan: [
        { label: 'Home Loan Sales',           value: 'home_loan_sales' },
        { label: 'Home Loan Credit',          value: 'home_loan_credit' },
        { label: 'Property Valuation',        value: 'home_loan_valuation' },
        { label: 'Home Loan Legal',           value: 'home_loan_legal' },
        { label: 'Home Loan Documentation',   value: 'home_loan_documentation' },
    ],
    personal_loan: [
        { label: 'Personal Loan Sales',       value: 'personal_loan_sales' },
        { label: 'Personal Loan Credit',      value: 'personal_loan_credit' },
        { label: 'Income Verification',       value: 'personal_loan_income' },
        { label: 'Personal Loan Underwriting', value: 'personal_loan_underwriting' },
    ],
    business_loan: [
        { label: 'Business Loan Sales',       value: 'business_loan_sales' },
        { label: 'Business Loan Credit',      value: 'business_loan_credit' },
        { label: 'Business Financial Analysis', value: 'business_loan_financial' },
        { label: 'MSME Loan Expert',          value: 'business_loan_msme' },
        { label: 'Business Loan Legal',       value: 'business_loan_legal' },
    ],
    car_loan: [
        { label: 'Car Loan Sales',            value: 'car_loan_sales' },
        { label: 'Car Loan Credit',           value: 'car_loan_credit' },
        { label: 'Vehicle Valuation',         value: 'car_loan_valuation' },
    ],
    education_loan: [
        { label: 'Education Loan Sales',      value: 'education_loan_sales' },
        { label: 'Education Loan Credit',     value: 'education_loan_credit' },
        { label: 'Education Counseling',      value: 'education_loan_counseling' },
    ],
    gold_loan: [
        { label: 'Gold Loan Sales',           value: 'gold_loan_sales' },
        { label: 'Gold Valuation',            value: 'gold_loan_valuation' },
        { label: 'Gold Loan Operations',      value: 'gold_loan_operations' },
    ],
    property_loan: [
        { label: 'LAP Sales',                 value: 'property_loan_sales' },
        { label: 'LAP Credit',                value: 'property_loan_credit' },
        { label: 'LAP Valuation',             value: 'property_loan_valuation' },
        { label: 'LAP Legal',                 value: 'property_loan_legal' },
    ],
    credit_card: [
        { label: 'Credit Card Sales',         value: 'credit_card_sales' },
        { label: 'CC Underwriting',           value: 'credit_card_underwriting' },
        { label: 'CC Risk Management',        value: 'credit_card_risk' },
    ],
    working_capital: [
        { label: 'Working Capital Sales',     value: 'working_capital_sales' },
        { label: 'Working Capital Credit',    value: 'working_capital_credit' },
        { label: 'Cash Flow Analysis',        value: 'working_capital_cashflow' },
    ],
    msme_loan: [
        { label: 'MSME Loan Sales',           value: 'msme_loan_sales' },
        { label: 'MSME Loan Credit',          value: 'msme_loan_credit' },
        { label: 'MSME Financial Analysis',   value: 'msme_loan_financial' },
    ],
};

// Flat list of every expertise value, with a category prefix as label group.
export const EXPERTISE_GROUPS = Object.entries(PRODUCT_EXPERTISE_MAP).map(
    ([productType, items]) => ({
        label: productLabel(productType),
        items,
    })
);

export const expertiseLabel = (value) => {
    for (const items of Object.values(PRODUCT_EXPERTISE_MAP)) {
        const found = items.find((it) => it.value === value);
        if (found) return found.label;
    }
    return value;
};

// ─── Service areas (cities + states + national) ──────────────────────────
export const SERVICE_AREAS = [
    // Metro Cities
    { label: 'Mumbai, Maharashtra',       value: 'mumbai_mh',         group: 'Metro Cities' },
    { label: 'Delhi NCR',                 value: 'delhi_ncr',         group: 'Metro Cities' },
    { label: 'Bangalore, Karnataka',      value: 'bangalore_ka',      group: 'Metro Cities' },
    { label: 'Hyderabad, Telangana',      value: 'hyderabad_tg',      group: 'Metro Cities' },
    { label: 'Chennai, Tamil Nadu',       value: 'chennai_tn',        group: 'Metro Cities' },
    { label: 'Kolkata, West Bengal',      value: 'kolkata_wb',        group: 'Metro Cities' },
    { label: 'Pune, Maharashtra',         value: 'pune_mh',           group: 'Metro Cities' },
    // Tier 1 Cities
    { label: 'Ahmedabad, Gujarat',        value: 'ahmedabad_gj',      group: 'Tier 1 Cities' },
    { label: 'Surat, Gujarat',            value: 'surat_gj',          group: 'Tier 1 Cities' },
    { label: 'Jaipur, Rajasthan',         value: 'jaipur_rj',         group: 'Tier 1 Cities' },
    { label: 'Lucknow, Uttar Pradesh',    value: 'lucknow_up',        group: 'Tier 1 Cities' },
    { label: 'Kanpur, Uttar Pradesh',     value: 'kanpur_up',         group: 'Tier 1 Cities' },
    { label: 'Nagpur, Maharashtra',       value: 'nagpur_mh',         group: 'Tier 1 Cities' },
    { label: 'Indore, Madhya Pradesh',    value: 'indore_mp',         group: 'Tier 1 Cities' },
    { label: 'Thane, Maharashtra',        value: 'thane_mh',          group: 'Tier 1 Cities' },
    { label: 'Bhopal, Madhya Pradesh',    value: 'bhopal_mp',         group: 'Tier 1 Cities' },
    { label: 'Visakhapatnam, AP',         value: 'visakhapatnam_ap',  group: 'Tier 1 Cities' },
    { label: 'Patna, Bihar',              value: 'patna_br',          group: 'Tier 1 Cities' },
    { label: 'Vadodara, Gujarat',         value: 'vadodara_gj',       group: 'Tier 1 Cities' },
    { label: 'Ghaziabad, Uttar Pradesh',  value: 'ghaziabad_up',      group: 'Tier 1 Cities' },
    { label: 'Ludhiana, Punjab',          value: 'ludhiana_pb',       group: 'Tier 1 Cities' },
    { label: 'Agra, Uttar Pradesh',       value: 'agra_up',           group: 'Tier 1 Cities' },
    { label: 'Nashik, Maharashtra',       value: 'nashik_mh',         group: 'Tier 1 Cities' },
    { label: 'Coimbatore, Tamil Nadu',    value: 'coimbatore_tn',     group: 'Tier 1 Cities' },
    { label: 'Kochi, Kerala',             value: 'kochi_kl',          group: 'Tier 1 Cities' },
    // State-wide
    { label: 'All Maharashtra',           value: 'state_maharashtra', group: 'State-wide' },
    { label: 'All Gujarat',               value: 'state_gujarat',     group: 'State-wide' },
    { label: 'All Karnataka',             value: 'state_karnataka',   group: 'State-wide' },
    { label: 'All Tamil Nadu',            value: 'state_tamilnadu',   group: 'State-wide' },
    { label: 'All Telangana',             value: 'state_telangana',   group: 'State-wide' },
    { label: 'All Andhra Pradesh',        value: 'state_andhrapradesh', group: 'State-wide' },
    { label: 'All Uttar Pradesh',         value: 'state_uttarpradesh', group: 'State-wide' },
    { label: 'All West Bengal',           value: 'state_westbengal',  group: 'State-wide' },
    { label: 'All Rajasthan',             value: 'state_rajasthan',   group: 'State-wide' },
    { label: 'All Madhya Pradesh',        value: 'state_madhyapradesh', group: 'State-wide' },
    { label: 'All Punjab',                value: 'state_punjab',      group: 'State-wide' },
    { label: 'All Kerala',                value: 'state_kerala',      group: 'State-wide' },
    // National
    { label: 'All India (Pan-India)',     value: 'all_india',         group: 'National' },
];

export const SERVICE_AREA_GROUPS = (() => {
    const groups = new Map();
    SERVICE_AREAS.forEach((a) => {
        if (!groups.has(a.group)) groups.set(a.group, []);
        groups.get(a.group).push({ label: a.label, value: a.value });
    });
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
})();

// ─── Banking skills ──────────────────────────────────────────────────────
export const BANK_SKILLS = [
    // Credit & Risk
    { label: 'Credit Analysis',                value: 'credit_analysis',         category: 'Credit & Risk' },
    { label: 'Risk Assessment',                value: 'risk_assessment',         category: 'Credit & Risk' },
    { label: 'Credit Scoring',                 value: 'credit_scoring',          category: 'Credit & Risk' },
    { label: 'CIBIL Analysis',                 value: 'cibil_analysis',          category: 'Credit & Risk' },
    { label: 'Bureau Report Analysis',         value: 'bureau_report_analysis',  category: 'Credit & Risk' },
    // Loan Operations
    { label: 'Loan Processing',                value: 'loan_processing',         category: 'Loan Operations' },
    { label: 'Loan Underwriting',              value: 'loan_underwriting',       category: 'Loan Operations' },
    { label: 'Loan Documentation',             value: 'loan_documentation',      category: 'Loan Operations' },
    { label: 'Loan Disbursement',              value: 'loan_disbursement',       category: 'Loan Operations' },
    { label: 'KYC Verification',               value: 'kyc_verification',        category: 'Loan Operations' },
    // Sales & Relationship
    { label: 'Relationship Management',        value: 'relationship_management', category: 'Sales & Relationship' },
    { label: 'Customer Acquisition',           value: 'customer_acquisition',    category: 'Sales & Relationship' },
    { label: 'Cross-selling',                  value: 'cross_selling',           category: 'Sales & Relationship' },
    { label: 'Lead Generation',                value: 'lead_generation',         category: 'Sales & Relationship' },
    { label: 'Sales Negotiation',              value: 'sales_negotiation',       category: 'Sales & Relationship' },
    // Financial Analysis
    { label: 'Financial Analysis',             value: 'financial_analysis',      category: 'Financial Analysis' },
    { label: 'Income Verification',            value: 'income_verification',     category: 'Financial Analysis' },
    { label: 'Bank Statement Analysis',        value: 'bank_statement_analysis', category: 'Financial Analysis' },
    { label: 'Financial Modeling',             value: 'financial_modeling',      category: 'Financial Analysis' },
    { label: 'DTI Ratio Analysis',             value: 'dti_analysis',            category: 'Financial Analysis' },
    // Compliance
    { label: 'Banking Compliance',             value: 'banking_compliance',      category: 'Compliance & Legal' },
    { label: 'RBI Guidelines',                 value: 'rbi_guidelines',          category: 'Compliance & Legal' },
    { label: 'Anti-Money Laundering',          value: 'aml',                     category: 'Compliance & Legal' },
    { label: 'Legal Documentation',            value: 'legal_documentation',     category: 'Compliance & Legal' },
    { label: 'FEMA Compliance',                value: 'fema_compliance',         category: 'Compliance & Legal' },
    // Product Knowledge
    { label: 'Home Loan Products',             value: 'home_loan_products',      category: 'Product Knowledge' },
    { label: 'Personal Loan Products',         value: 'personal_loan_products',  category: 'Product Knowledge' },
    { label: 'Business Loan Products',         value: 'business_loan_products',  category: 'Product Knowledge' },
    { label: 'Credit Card Products',           value: 'credit_card_products',    category: 'Product Knowledge' },
    { label: 'Insurance Products',             value: 'insurance_products',      category: 'Product Knowledge' },
    // Technical
    { label: 'Core Banking Systems',           value: 'core_banking_systems',    category: 'Technical' },
    { label: 'Loan Management Software',       value: 'loan_management_software', category: 'Technical' },
    { label: 'CRM Systems',                    value: 'crm_systems',             category: 'Technical' },
    { label: 'MS Excel (Advanced)',            value: 'ms_excel_advanced',       category: 'Technical' },
    { label: 'Financial Software',             value: 'financial_software',      category: 'Technical' },
    // Customer Service
    { label: 'Customer Service',               value: 'customer_service',        category: 'Customer Service' },
    { label: 'Client Communication',           value: 'client_communication',    category: 'Customer Service' },
    { label: 'Problem Solving',                value: 'problem_solving',         category: 'Customer Service' },
    { label: 'Query Resolution',               value: 'query_resolution',        category: 'Customer Service' },
    { label: 'Complaint Handling',             value: 'complaint_handling',      category: 'Customer Service' },
];

export const SKILL_GROUPS = (() => {
    const groups = new Map();
    BANK_SKILLS.forEach((s) => {
        if (!groups.has(s.category)) groups.set(s.category, []);
        groups.get(s.category).push({ label: s.label, value: s.value });
    });
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
})();

export const skillLabel = (value) =>
    BANK_SKILLS.find((s) => s.value === value)?.label || value;

// ─── Languages ───────────────────────────────────────────────────────────
export const LANGUAGES = [
    // National
    { label: 'English',   value: 'english',   region: 'National' },
    { label: 'Hindi',     value: 'hindi',     region: 'National' },
    // South
    { label: 'Tamil',     value: 'tamil',     region: 'South Indian' },
    { label: 'Telugu',    value: 'telugu',    region: 'South Indian' },
    { label: 'Kannada',   value: 'kannada',   region: 'South Indian' },
    { label: 'Malayalam', value: 'malayalam', region: 'South Indian' },
    // West
    { label: 'Marathi',   value: 'marathi',   region: 'West Indian' },
    { label: 'Gujarati',  value: 'gujarati',  region: 'West Indian' },
    { label: 'Konkani',   value: 'konkani',   region: 'West Indian' },
    { label: 'Sindhi',    value: 'sindhi',    region: 'West Indian' },
    // East
    { label: 'Bengali',   value: 'bengali',   region: 'East Indian' },
    { label: 'Odia',      value: 'odia',      region: 'East Indian' },
    { label: 'Assamese',  value: 'assamese',  region: 'East Indian' },
    // North
    { label: 'Punjabi',   value: 'punjabi',   region: 'North Indian' },
    { label: 'Urdu',      value: 'urdu',      region: 'North Indian' },
    { label: 'Kashmiri',  value: 'kashmiri',  region: 'North Indian' },
];

export const LANGUAGE_GROUPS = (() => {
    const groups = new Map();
    LANGUAGES.forEach((l) => {
        if (!groups.has(l.region)) groups.set(l.region, []);
        groups.get(l.region).push({ label: l.label, value: l.value });
    });
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
})();

export const languageLabel = (value) =>
    LANGUAGES.find((l) => l.value === value)?.label || value;
